import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createAxiosInstance } from "../axiosSettings";

// Initial state
const initialState = {
    categories: [],
    loading: false,
    error: null,
};

// Helper: transform service object
const transformService = (service) => {
    const id = service._id || service.id;
    const name = service.name || '';
    const baseURL = (process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api').replace('/api', '');

    let imageUrl = null;
    const imageFields = [
        service.image,
        service.imageUrl,
        service.imagePath,
        service.photo,
        service.photoUrl,
        service.picture,
        service.pictureUrl
    ];

    for (const field of imageFields) {
        if (field !== null && field !== undefined) {
            if (typeof field === 'object' && field !== null) {
                imageUrl = field.url || field.path || field.src || field.image || field.imageUrl || null;
                if (imageUrl) break;
            } else if (typeof field === 'string' && field.trim() !== '') {
                imageUrl = field;
                break;
            }
        }
    }

    if (imageUrl && typeof imageUrl === 'string') {
        const trimmed = imageUrl.trim();
        if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') {
            imageUrl = null;
        } else if (trimmed.startsWith('http') || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
            const urlObj = new URL(trimmed);
            const pathname = urlObj.pathname;
            if (
                pathname.startsWith('/') &&
                !pathname.startsWith('/uploads/') &&
                !pathname.startsWith('/images/') &&
                !pathname.startsWith('/public/') &&
                pathname !== '/' &&
                pathname.split('/').length === 2
            ) {
                urlObj.pathname = `/uploads${pathname}`;
                imageUrl = urlObj.toString();
            } else {
                imageUrl = trimmed;
            }
        } else {
            let imagePath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
            if (!imagePath.startsWith('/uploads/') && !imagePath.startsWith('/images/') && !imagePath.startsWith('/public/')) {
                imagePath = `/uploads${imagePath}`;
            }
            imageUrl = `${baseURL}${imagePath}`;
        }
    } else {
        imageUrl = null;
    }

    const status = service.status ?? service.isActive;
    const isActive =
        typeof status === 'number'
            ? status === 1
            : typeof status === 'string'
                ? status.toLowerCase() === 'active'
                : Boolean(status);

    return { ...service, id, name, image: imageUrl, isActive };
};

// Async thunks
export const fetchCategories = createAsyncThunk('categories/fetchAll', async (_, { rejectWithValue }) => {
    try {
        const axiosInstance = await createAxiosInstance();
        const { data } = await axiosInstance.get('/admin/categories');
        return Array.isArray(data) ? data.map(transformService) : [];
    } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
    }
});

export const createCategory = createAsyncThunk('categories/create', async (payload, { rejectWithValue }) => {
    try {
        const formData = new FormData();
        const serviceName = payload.name.trim();

        if (!serviceName) {
            return rejectWithValue('Service name cannot be empty');
        }

        formData.append('name', serviceName);
        formData.append('status', payload.isActive ? 1 : 0);

        if (payload.image) {
            if (payload.image instanceof File || payload.image instanceof Blob) {
                formData.append('image', payload.image);
            } else if (typeof payload.image === 'string' && payload.image.trim() !== '') {
                formData.append('image', payload.image);
            }
        }
        const axiosInstance = await createAxiosInstance();

        const response = await axiosInstance.post('/admin/categories', formData);
        let serviceData = response.data;
        if (serviceData && (serviceData.data || serviceData.service)) {
            serviceData = serviceData.data || serviceData.service;
        }

        return transformService(serviceData);
    } catch (err) {
        const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create service';
        return rejectWithValue(errorMessage);
    }
});

export const updateCategory = createAsyncThunk('categories/update', async ({ id, data }, { rejectWithValue }) => {
    try {
        const formData = new FormData();
        if (data.name) formData.append('name', data.name);
        if (data.isActive !== undefined) formData.append('status', data.isActive ? 1 : 0);
        if (data.image) formData.append('image', data.image);

        const serviceId = id || data._id || data.id;
        const axiosInstance = await createAxiosInstance();
        const response = await axiosInstance.post(`/admin/categories/update/${serviceId}`, formData);
        return response.data;
    } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
    }
});

export const deleteCategory = createAsyncThunk('categories/delete', async (id, { rejectWithValue }) => {
    try {
        const axiosInstance = await createAxiosInstance();
        await axiosInstance.delete(`/admin/categories/${id}`);
        return id;
    } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
    }
});

export const toggleCategoryStatus = createAsyncThunk('categories/toggleStatus', async ({ id, isActive }, { rejectWithValue }) => {
    try {
        const axiosInstance = await createAxiosInstance();
        const { data } = await axiosInstance.post(
            `/admin/categories/update/${id}`,
            { status: isActive ? 1 : 0 },
            { headers: { 'Content-Type': 'application/json' } }
        );
        return data;
    } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
    }
});

// Slice
const categoriesSlice = createSlice({
    name: 'categories',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCategories.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = action.payload;
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createCategory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createCategory.fulfilled, (state, action) => {
                state.loading = false;
                const newService = action.payload;
                if (newService && !state.categories.some((s) => s.id === newService.id || s._id === newService.id)) {
                    state.categories.push(newService);
                }
            })
            .addCase(createCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateCategory.fulfilled, (state, action) => {
                const updated = transformService(action.payload);
                const index = state.categories.findIndex((s) => s.id === updated.id);
                if (index >= 0) state.categories[index] = updated;
                else state.categories.push(updated);
            })
            .addCase(updateCategory.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(deleteCategory.fulfilled, (state, action) => {
                state.categories = state.categories.filter((s) => s.id !== action.payload && s._id !== action.payload);
            })
            .addCase(deleteCategory.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(toggleCategoryStatus.fulfilled, (state, action) => {
                const updated = transformService(action.payload);
                const index = state.categories.findIndex((s) => s.id === updated.id);
                if (index >= 0) state.categories[index] = updated;
            })
            .addCase(toggleCategoryStatus.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

// Exports
export const { clearError, resetState } = categoriesSlice.actions;
export const selectCategories = (state) => state?.categories?.categories;
export const selectCategoriesLoading = (state) => state?.categories?.loading;
export const selectCategoriesError = (state) => state?.categories?.error;
export const selectServiceById = (id) => (state) => state?.categories?.categories.find((s) => s.id === id);

export default categoriesSlice.reducer;
